import { useEffect, useState } from "react";
import { api, fmtDate } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { PageHeader } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, MagnifyingGlass } from "@phosphor-icons/react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ORDER_CREATOR_ROLES } from "@/lib/constants";

const STATUS_LABEL = {
  draft: "Draft",
  pending_sales: "Pending — Sales Mgr",
  pending_finance: "Pending — Finance Mgr",
  approved: "Approved",
  rejected: "Rejected",
};
const STATUS_COLOR = {
  draft: "bg-slate-200 text-slate-800 dark:bg-slate-700 dark:text-slate-200",
  pending_sales: "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200",
  pending_finance: "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200",
  approved: "bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-200",
  rejected: "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-200",
};

export default function Orders() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const canDelete = user?.role === "super_admin";

  const load = () => api.get("/orders", { params: { q: q || undefined } }).then((r) => setItems(r.data));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [q]);

  const remove = async (o) => {
    if (!window.confirm(`Delete order ${o.order_no}? This cannot be undone.`)) return;
    try { await api.delete(`/orders/${o.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div data-testid="orders-page">
      <PageHeader
        title="Order Creation"
        description="Create and track customer orders through Sales Manager and Finance Manager approvals."
        action={
          ORDER_CREATOR_ROLES.includes(user?.role) && (
            <Button className="btn-brand" onClick={() => nav("/orders/new")} data-testid="new-order-btn">
              <Plus size={16} className="mr-1.5" />New Order
            </Button>
          )
        }
      />
      <div className="flex items-center gap-2 mb-4">
        <div className="flex items-center gap-2 flex-1 max-w-md bg-white dark:bg-slate-800 border rounded-md px-3 py-1.5">
          <MagnifyingGlass size={16} className="opacity-40" />
          <input placeholder="Search order no, customer, PO…" value={q} onChange={(e) => setQ(e.target.value)}
                 className="bg-transparent outline-none text-sm w-full" data-testid="orders-search" />
        </div>
      </div>
      <Card className="p-0 overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Order No</TableHead>
              <TableHead>Customer</TableHead>
              <TableHead>Opportunity</TableHead>
              <TableHead>Customer PO No</TableHead>
              <TableHead>Sell Total</TableHead>
              <TableHead>Margin</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Created</TableHead>
              {canDelete && <TableHead className="text-right">Actions</TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.map((o) => (
              <TableRow key={o.id} className="cursor-pointer" onClick={(e) => {
                if (e.target.closest("[data-stop]")) return;
                nav(`/orders/${o.id}`);
              }}>
                <TableCell className="font-mono text-xs">{o.order_no}</TableCell>
                <TableCell className="font-medium">{o.customer_name}</TableCell>
                <TableCell className="text-sm">{o.opportunity_name || "—"}</TableCell>
                <TableCell className="text-xs font-mono">{o.customer_po_number}</TableCell>
                <TableCell className="text-sm">₹ {(o.totals?.sell_total || 0).toLocaleString("en-IN")}</TableCell>
                <TableCell className="text-sm">₹ {(o.totals?.margin_total || 0).toLocaleString("en-IN")} <span className="text-xs opacity-60">({o.totals?.margin_pct || 0}%)</span></TableCell>
                <TableCell>
                  <span className={`text-[11px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded ${STATUS_COLOR[o.status] || ""}`}>
                    {STATUS_LABEL[o.status] || o.status}
                  </span>
                </TableCell>
                <TableCell className="text-xs opacity-60">{fmtDate(o.created_at)}</TableCell>
                {canDelete && (
                  <TableCell className="text-right" data-stop onClick={(e) => e.stopPropagation()}>
                    <Button size="sm" variant="outline" className="text-red-600" onClick={() => remove(o)} data-testid={`order-del-${o.id}`}>Delete</Button>
                  </TableCell>
                )}
              </TableRow>
            ))}
            {items.length === 0 && (
              <TableRow><TableCell colSpan={canDelete ? 9 : 8} className="text-center text-sm opacity-60 py-10">No orders yet.</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}
